'use client';
import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';
import { useCurrency } from '@/context/CurrencyContext';

export interface Property {
  id: number;
  title: string;
  price: string;
  area: string;
  land_area?: string;
  gfa?: string;
  status: string;
  label: string;
  image: string;
  alt: string;
  href: string;
  type?: string;
  pl_score_total?: number;
  description?: string;
  badges?: Array<{
    badge_show_on_property_images: boolean;
    badge_color: string;
    label_name: string;
  }>;
  neighborhood?: string;
  city?: string;
}

export const formatAreaValue = (val: string | number | null | undefined) => {
  if (val === null || val === undefined || val === '') return '';
  const str = val.toString().trim();
  const clean = str.replace(/[^0-9.]/g, '');
  const num = parseFloat(clean);
  if (!isNaN(num)) {
    return num.toLocaleString('en-US');
  }
  return str;
};

export default function PropertyCard({
  property,
  viewMode = 'grid',
  onQuickView,
}: {
  property: Property;
  viewMode?: 'grid' | 'list';
  onQuickView?: (property: Property) => void;
}) {
  const { formatPrice } = useCurrency();
  // Use dynamic pl_score_total if present, otherwise calculate deterministic score
  let score = property.pl_score_total;
  if (score === undefined || score === null || score === 0) {
    const lowerTitle = property.title.toLowerCase();
    if (lowerTitle.includes('meydan')) {
      score = 60;
    } else if (lowerTitle.includes('hotel plot')) {
      score = 90;
    } else if (lowerTitle.includes('villa plots')) {
      score = 85;
    } else if (lowerTitle.includes('jebel ali hills')) {
      score = 90;
    } else if (lowerTitle.includes('developer plot')) {
      score = 80 + (property.id % 11);
    } else {
      score = 80 + (property.id % 16);
    }
  }

  // Ensure score is within realistic bounds
  score = Math.max(60, Math.min(100, Number(score)));

  const isSold =
    property.status?.split(',').some((s) => s.trim().toLowerCase() === 'sold') || false;

  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-none shadow-[0_2px_15px_-3px_rgba(0,0,0,0.05),0_10px_20px_-2px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300 overflow-hidden border border-slate-100 flex flex-col md:flex-row h-full md:h-[240px] group w-full">
        {/* Image Container */}
        <div className="relative overflow-hidden w-full md:w-80 h-60 md:h-full bg-slate-50 flex-shrink-0">
          <Link href={property.href} className="absolute inset-0 z-[1] cursor-pointer">
            <AppImage
              src={property.image}
              alt={property.alt}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-500"
              sizes="(max-width: 768px) 100vw, 320px"
            />
          </Link>

          {/* Gradients to ensure text readability — pointer-events-none so clicks pass through to Link */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none z-[2]"></div>

          {/* SVG Distressed Stamp Filter definition (hidden but scoped) */}
          <svg className="absolute w-0 h-0" width="0" height="0">
            <defs>
              <filter id="stamp-grunge-list">
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.7"
                  numOctaves="5"
                  result="noise"
                />
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="noise"
                  scale="5.5"
                  xChannelSelector="R"
                  yChannelSelector="G"
                />
              </filter>
            </defs>
          </svg>

          {/* Distressed "Sold" Seal stamp overlay */}
          {isSold && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/15 z-[5] backdrop-blur-[0.5px] pointer-events-none select-none">
              <div
                className="relative flex items-center justify-center w-40 h-20 border-4 border-double border-white flex-shrink-0 rotate-[-12deg] bg-black/40 backdrop-blur-[0.5px] shadow-[0_0_20px_rgba(255,255,255,0.6),inset_0_0_20px_rgba(255,255,255,0.4)]"
                style={{ filter: 'url(#stamp-grunge-list)' }}
              >
                <div className="w-full h-full border-2 border-white flex items-center justify-center p-2">
                  <span className="text-3xl font-black tracking-widest text-white leading-none">
                    SOLD
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Top Left Badges */}
          {property.badges && property.badges.length > 0 && (
            <div className="absolute top-3 left-3 flex flex-col items-start gap-1 z-[10]">
              {property.badges.map((badge, index) => (
                <span
                  key={index}
                  className="text-white text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] shadow-sm transition-transform duration-300 hover:scale-105"
                  style={{ backgroundColor: badge.badge_color || '#82BC41' }}
                >
                  {badge.label_name}
                </span>
              ))}
            </div>
          )}

          {/* Quick-view expand button */}
          <div className="absolute bottom-3 right-3 z-[10]">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onQuickView) onQuickView(property);
              }}
              className="text-white hover:text-[#7fc142] transition-colors p-1 bg-black/30 hover:bg-black/50 rounded"
              title="Quick View"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-5 flex flex-col justify-between flex-grow">
          <div>
            <div className="flex justify-between items-start gap-4 mb-2">
              <h2 className="font-bold text-[#1a1a1a] text-[15px] leading-snug hover:text-[#006838] transition-colors line-clamp-2">
                <Link href={property.href}>{property.title}</Link>
              </h2>

              {/* Dynamic Plot Score Circle Gauge & Badge */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div className="relative w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-md border border-slate-100/50 transition-transform duration-300 group-hover:scale-105">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="24"
                      cy="24"
                      r="19"
                      className="stroke-slate-100"
                      strokeWidth="3.5"
                      fill="transparent"
                    />
                    <circle
                      cx="24"
                      cy="24"
                      r="19"
                      className="stroke-[#006838]"
                      strokeWidth="3.5"
                      fill="transparent"
                      strokeDasharray={119.38}
                      strokeDashoffset={119.38 - (score / 100) * 119.38}
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className="absolute text-[13px] font-black text-[#006838] leading-none">
                    {score}
                  </span>
                </div>
              </div>
            </div>

            {/* Price */}
            <div className="text-[#006838] font-black text-lg tracking-wide mb-3">
              {formatPrice(property.price)}
            </div>

            {/* Metadata Row */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 text-slate-500 text-[12px] font-medium border-b border-slate-100 pb-3">
              {property.land_area && (
                <span className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 4v16M15 4v16M4 9h16M4 15h16"
                    />
                  </svg>
                  <span>{formatAreaValue(property.land_area)} sq. ft.</span>
                </span>
              )}
              {property.gfa && (
                <span className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <span>{formatAreaValue(property.gfa)} sq. ft. GFA</span>
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center">
            {/* Categories tag row at the bottom */}
            {property.type && (
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#006838]/85 hover:text-[#006838] transition-colors leading-relaxed">
                {property.type}
              </div>
            )}

            <Link
              href={property.href}
              className="text-white bg-[#006838] hover:bg-[#005a30] transition-all duration-150 text-[11px] font-bold uppercase tracking-widest px-4 py-2 hover:shadow-md"
            >
              View Details
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-none shadow-[0_2px_15px_-3px_rgba(0,0,0,0.05),0_10px_20px_-2px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300 overflow-hidden border border-slate-100 flex flex-col h-full group">
      {/* Image Container */}
      <div className="relative overflow-hidden w-full aspect-[4/3] bg-slate-50">
        {/* Image link — z-[1] so it's below overlays but clickable */}
        <Link href={property.href} className="absolute inset-0 z-[1] cursor-pointer">
          <AppImage
            src={property.image}
            alt={property.alt}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-500"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </Link>

        {/* Gradients — pointer-events-none so clicks pass through to Link */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none z-[2]"></div>

        {/* SVG Distressed Stamp Filter definition (hidden but scoped) */}
        <svg className="absolute w-0 h-0" width="0" height="0">
          <defs>
            <filter id="stamp-grunge">
              <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="5" result="noise" />
              <feDisplacementMap
                in="SourceGraphic"
                in2="noise"
                scale="5.5"
                xChannelSelector="R"
                yChannelSelector="G"
              />
            </filter>
          </defs>
        </svg>

        {/* Distressed "Sold" Seal stamp overlay */}
        {isSold && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/15 z-[5] backdrop-blur-[0.5px] pointer-events-none select-none">
            <div
              className="relative flex items-center justify-center w-48 h-24 border-[6px] border-double border-white flex-shrink-0 rotate-[-12deg] bg-black/40 backdrop-blur-[0.5px] shadow-[0_0_30px_rgba(255,255,255,0.6),inset_0_0_30px_rgba(255,255,255,0.4)]"
              style={{ filter: 'url(#stamp-grunge)' }}
            >
              <div className="w-full h-full border-[3px] border-white flex items-center justify-center p-4">
                <span className="text-5xl font-black tracking-widest text-white leading-none animate-pulse">
                  SOLD
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Top Left Badges */}
        {property.badges && property.badges.length > 0 && (
          <div className="absolute top-4 left-4 flex flex-col items-start gap-1.5 z-[10]">
            {property.badges.map((badge, index) => (
              <span
                key={index}
                className="text-white text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-[2px] shadow-md transition-transform duration-300 hover:scale-105"
                style={{ backgroundColor: badge.badge_color || '#82BC41' }}
              >
                {badge.label_name}
              </span>
            ))}
          </div>
        )}

        {/* Dynamic Plot Score Circle Gauge & Badge */}
        <div className="absolute top-4 right-4 flex flex-col items-center z-[10]">
          <div className="relative w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg border border-slate-100/50 transition-transform duration-300 group-hover:scale-105">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="28"
                cy="28"
                r="22"
                className="stroke-slate-100"
                strokeWidth="4.5"
                fill="transparent"
              />
              <circle
                cx="28"
                cy="28"
                r="22"
                className="stroke-[#006838]"
                strokeWidth="4.5"
                fill="transparent"
                strokeDasharray={138.23}
                strokeDashoffset={138.23 - (score / 100) * 138.23}
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute text-[15px] font-black text-[#006838] tracking-tight leading-none">
              {score}
            </span>
          </div>
          <div className="bg-[#006838] text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] shadow-md mt-1 whitespace-nowrap opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 pointer-events-none">
            Deal Score
          </div>
        </div>

        {/* Bottom Overlay: Price + Quick-view button */}
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between z-[10]">
          {/* Price — click passes through to Link via pointer-events-none on price span */}
          <span className="text-white font-black text-lg tracking-wide drop-shadow-md pointer-events-none">
            {formatPrice(property.price)}
          </span>
          {/* Quick-view button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onQuickView) onQuickView(property);
            }}
            className="text-white hover:text-[#7fc142] transition-colors p-1 bg-black/30 hover:bg-black/50 rounded"
            title="Quick View"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col justify-between flex-grow">
        <div>
          {/* Title */}
          <h2 className="font-bold text-[#1a1a1a] text-[15px] leading-snug mb-4 hover:text-[#006838] transition-colors min-h-[44px] line-clamp-2">
            <Link href={property.href}>{property.title}</Link>
          </h2>

          {/* Metadata Row */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 text-slate-500 text-[12px] font-medium border-b border-slate-100 pb-4">
            {property.land_area && (
              <span className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 4v16M15 4v16M4 9h16M4 15h16"
                  />
                </svg>
                <span>{formatAreaValue(property.land_area)} sq. ft.</span>
              </span>
            )}
            {property.gfa && (
              <span className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <span>{formatAreaValue(property.gfa)} sq. ft. GFA</span>
              </span>
            )}
          </div>
        </div>

        {/* Categories tag row at the bottom */}
        {property.type && (
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#006838]/85 hover:text-[#006838] transition-colors leading-relaxed">
            {property.type}
          </div>
        )}
      </div>
    </div>
  );
}
